export type ConfirmTone = "default" | "destructive";
export type ConfirmMode = "confirm" | "alert";

export type ConfirmOptions = {
  readonly title: string;
  readonly description?: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly tone?: ConfirmTone;
};

export type QueuedConfirm = ConfirmOptions & {
  readonly id: number;
  readonly mode: ConfirmMode;
  readonly resolve: (confirmed: boolean) => void;
};

const listeners = new Set<() => void>();
let nextId = 1;
let queue: readonly QueuedConfirm[] = [];

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeConfirmQueue(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getConfirmQueueSnapshot(): readonly QueuedConfirm[] {
  return queue;
}

const EMPTY_QUEUE: readonly QueuedConfirm[] = [];

export function getConfirmQueueServerSnapshot(): readonly QueuedConfirm[] {
  return EMPTY_QUEUE;
}

function enqueue(options: ConfirmOptions, mode: ConfirmMode): Promise<boolean> {
  return new Promise((resolve) => {
    const item: QueuedConfirm = {
      id: nextId,
      mode,
      title: options.title,
      resolve,
      ...(options.description !== undefined ? { description: options.description } : {}),
      ...(options.confirmLabel !== undefined ? { confirmLabel: options.confirmLabel } : {}),
      ...(options.cancelLabel !== undefined ? { cancelLabel: options.cancelLabel } : {}),
      ...(options.tone !== undefined ? { tone: options.tone } : {}),
    };
    nextId += 1;
    queue = [...queue, item];
    emit();
  });
}

export function resolveConfirm(id: number, confirmed: boolean): void {
  const item = queue.find((entry) => entry.id === id);
  if (item === undefined) return;
  queue = queue.filter((entry) => entry.id !== id);
  emit();
  item.resolve(confirmed);
}

/** Promise-based confirm. Destructive actions must pass `tone: "destructive"`. */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  return enqueue(options, "confirm");
}

/** Single-action acknowledgement routed through the same queued host. */
export async function alertDialog(
  options: Omit<ConfirmOptions, "cancelLabel" | "tone">,
): Promise<void> {
  await enqueue(options, "alert");
}

export function resetConfirmQueue(): void {
  const pending = queue;
  queue = EMPTY_QUEUE;
  emit();
  for (const item of pending) item.resolve(false);
}
